// @flow

import glamorous from 'glamorous';
import numeral from 'numeral';
import React, { Component } from 'react';
import { get, sumBy } from 'lodash/fp';
import * as rapidfireApi from 'app/api/rapidfire';
import t from 'app/utils/translate';
import { Redirect } from 'react-router-dom';
import getSelections from 'app/utils/rapidfireUtils';
import ChallengeTimeStatusInfo from '../../components/challenge/ChallengeTimeStatusInfo';
import OpenChallengeModal from '../../components/challenge/OpenChallengeModal';
import BackButton from '../../components/commons/BackButton';
import Button from '../../components/commons/Button';
import Title from '../../components/commons/Title';
import Container from '../../components/layout/Container';
import NotificationHandler from '../../components/notifications/NotificationHandler';
import withNotificationCallback from '../../components/notifications/withNotificationCallback';
import Responsive from '../../components/responsive/Responsive';
import Header from './Header';
import MatchUps from './MatchUps';
import Player from './Player';

type SubmitTypes = {
  backLink: string,
  challenges: Array<Object>,
  contestType: string,
  fees: Array<number>,
  hasLightBackground: boolean,
  getCurrentUser: () => void,
  matchUps: Array<Array<Object>>,
  payStructure: number,
  showHeader: boolean,
  sportId: number,
  startTime: string,
  isVisible: boolean,
  refreshData: () => void
};

type UpdateTypes = {
  entryId: string,
  hasLightBackground: boolean,
  matchUps: Array<Array<Object>>,
  myEntrySelections: Object,
  showHeader: boolean,
  startPoll: () => void,
  startTime: string,
  stopPoll: () => void
};

type Props =
  | {
      pxc?: Object,
      isSignedIn: boolean,
      history: Object
    }
  | SubmitTypes
  | UpdateTypes;

type State = {
  entrySuccess: boolean,
  errors: Array<Object>,
  isSubmitting: boolean,
  isUpdating: boolean,
  playerWidgets: Array<string>,
  selectedPrice: ?number,
  selections: Array<string>,
  showConfirmModal: boolean,
  willSubmit: boolean,
  willUpdate: boolean
};

function getPrizePool(contestType, price, payStructure) {
  if (price === 0) {
    return 1;
  }
  return contestType && payStructure ? price * payStructure : 0;
}

class RapidFireTeamSelectionBase extends Component<Props, State> {
  props: Props;

  constructor(props: Props) {
    super(props);
    this.state = {
    isUpdating: false,
    isSubmitting: false,
    entrySuccess: false,
    errors: [],
    entryId: props.entryId,
    playerWidgets: [],
    selections: props.myEntrySelections || [],
    selectedPrice: props.fees ? props.fees[0] : null,
    showConfirmModal: false,
    willSubmit: false,
    willUpdate: false
    }
  }

  componentDidMount() {
    if (this.props.startPoll) this.props.startPoll();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.isVisible &&
      this.props.challengeGroupId &&
      this.props.challengeGroupId !== nextProps.challengeGroupId
    ) {
      this.props.transmitNotification({
        id: 'CHALLENGE_LOCKED_KEY',
        message: "Sorry! We've changed one or more matchups. Please make your new selections.",
        type: 'warning'
      });
    }
  }

  componentWillUnmount() {
    if (this.props.stopPoll) this.props.stopPoll();
  }

  handleSelect = (index, playerId) => {
    this.setState(state => {
      const selections = [...state.selections];
      selections[index] = playerId;
      return {
        selections
      };
    });
  };

  handleSubmit = () => {
    const { challenges, contestType, getCurrentUser, matchUps } = this.props;
    const { selectedPrice, selections } = this.state;

    const challenge = challenges.find(
      ch => ch.cost === selectedPrice && ch.rapidfireType === `rapidfire_${contestType}`
    );
    const challengeId = challenge.id;

    const selection = getSelections(matchUps, selections);
    this.setState({ isSubmitting: true, willSubmit: false, errors: [] });

    rapidfireApi
      .submitEntry(challengeId, { selection })
      .then(res => {
        getCurrentUser();
        this.setState({ entryId: res.data.id, isSubmitting: false, entrySuccess: true });
      })
      .catch(e => {
        console.log('error', e);
        if (this.props.refreshData) {
          this.props.refreshData();
        }
        this.setState({ isSubmitting: false, entrySuccess: false, errors: e.errors.messages });
      });
  };

  handleUpdate = () => {
    this.setState({ isUpdating: true, willUpdate: false, errors: [] });

    const { matchUps } = this.props;
    const { selections } = this.state;

    const selection = getSelections(matchUps, selections);
    rapidfireApi
      .updateEntry(this.state.entryId, { selection })
      .then(res => {
        this.setState({ isUpdating: false });
      })
      .catch(e => {
        console.log('error', e);
        this.setState({ isUpdating: false, errors: e.errors.messages });
      });
  };

  handleToggleWidget = (playerId, opponentId) => {
    const { playerWidgets } = this.state;
    let updatedPlayerWidgets = [...playerWidgets];

    if (!playerWidgets.includes(playerId)) {
      updatedPlayerWidgets.push(playerId);
      if (opponentId) updatedPlayerWidgets.push(opponentId);
    } else {
      updatedPlayerWidgets = updatedPlayerWidgets.filter(
        id => id !== playerId && id !== opponentId
      );
    }

    this.setState({ playerWidgets: updatedPlayerWidgets });
  };

  render() {
    const {
      backLink,
      challengeName,
      contestType,
      fees,
      hasLightBackground,
      history,
      isSignedIn,
      pxc,
      matchUps,
      myEntrySelections,
      payStructure,
      showHeader,
      sportId,
      startTime,
      statusId,
      statusLabel,
      competitionId
    } = this.props;

    const {
      entryId,
      entrySuccess,
      errors,
      isSubmitting,
      isUpdating,
      playerWidgets,
      selectedPrice,
      selections,
      showConfirmModal,
      willSubmit,
      willUpdate
    } = this.state;

    const hasEntered = !!myEntrySelections;
    const numSelections = sumBy(s => !!s)(selections);
    const prizePool = getPrizePool(contestType, selectedPrice, payStructure);
    const hasError = errors.length > 0;

    if (entrySuccess && !showConfirmModal) {
      return <Redirect to="/rapidfire" />;
    }

    return (
      <Responsive desktop>
        {isDesktop => (
          <Container size="medium">
            {backLink && (
              <BackButton link={backLink} scheme={hasLightBackground ? 'dark' : 'light'}>
                Back
              </BackButton>
            )}
            {showHeader && (
              <Header>
                <Title>{challengeName}</Title>
                <ChallengeTimeStatusInfo
                  startTime={startTime}
                  statusId={statusId}
                  statusLabel={statusLabel}
                />
              </Header>
            )}
            <MatchUps
              matchUps={matchUps}
              renderPlayer={(player, opponent, index, isYou) => (
                <Responsive tablet>
                  {isTablet => (
                    <Player
                      competitionId={competitionId}
                      hideMatchUpLine={sportId === 99}
                      player={player}
                      selected={selections[index] === player.id}
                      reverseLayout={!isYou}
                      showWidget={playerWidgets.includes(player.id)}
                      toggleWidget={() => {
                        this.handleToggleWidget(player.id, !isTablet ? opponent.id : undefined);
                      }}
                      onSelect={() => {
                        this.handleSelect(index, player.id);
                      }}
                    />
                  )}
                </Responsive>
              )}
            />
            {fees && (
              <BottomSection>
                <EntryTitle>Select entry fee amount</EntryTitle>
                <SelectionAndReturns>
                  <Row>
                    {fees.map(fee => (
                      <PriceButton
                        key={`fee_${fee}`}
                        selected={selectedPrice === fee}
                        onClick={() => {
                          this.setState({ selectedPrice: fee });
                        }}
                      >
                        {fee > 0 ? numeral(fee).format('$0,0') : 'FREE'}
                      </PriceButton>
                    ))}
                  </Row>
                  <ReturnsWrapper>
                    Returns
                    <Prize>{numeral(prizePool).format('$0,0.[00]')}</Prize>
                  </ReturnsWrapper>
                </SelectionAndReturns>

                <ButtonsWrapper>
                  <Button
                    autoBorderRadius={isDesktop}
                    kind="success"
                    disabled={numSelections < 5}
                    onClick={() => {
                      this.setState(
                        {
                          showConfirmModal: true,
                          willSubmit: true
                        },
                        () => {
                          if (!isSignedIn) {
                            if (!pxc || !pxc.isEnabled) {
                              if (window.portal) window.portal.client.showLogin();
                            } else {
                              pxc.login();
                            }
                          }
                        }
                      );
                    }}
                    style={{ width: isDesktop ? 400 : '100%' }}
                  >
                    Confirm
                  </Button>
                </ButtonsWrapper>
              </BottomSection>
            )}
            {hasEntered && (
              <ButtonsWrapper>
                <Button
                  autoBorderRadius={isDesktop}
                  kind="success"
                  disabled={numSelections < 5}
                  onClick={() => {
                    this.setState({ showConfirmModal: true, willUpdate: true });
                  }}
                  style={{ width: isDesktop ? 400 : '100%' }}
                >
                  Update
                </Button>
              </ButtonsWrapper>
            )}
            {showConfirmModal && (
              <OpenChallengeModal
                cost={selectedPrice}
                startTime={startTime}
                showSubmitSuccess={entrySuccess}
                isSubmitting={isSubmitting}
                isUpdating={isUpdating}
                onCancel={() => {
                  this.setState({
                    showConfirmModal: false,
                    willSubmit: false,
                    willUpdate: false
                  });
                }}
                onUpdate={this.handleUpdate}
                onSubmit={this.handleSubmit}
                showEntryConfirmation={willSubmit}
                showUpdateConfirmation={willUpdate}
                submitMore={false}
                entryId={entryId}
                entryIdsToUpdate={[entryId]}
              />
            )}
            <NotificationHandler
              afterCond={!isSubmitting && hasError}
              beforeCond={isSubmitting}
              persist={hasError}
              type="danger"
            >
              <span>
                Your submission failed
                <br />
                {get('[0].message', errors)}
              </span>
            </NotificationHandler>
            <NotificationHandler
              afterCond={!isUpdating}
              beforeCond={isUpdating}
              persist={hasError}
              type={hasError ? 'danger' : 'success'}
            >
              {hasError ? (
                <span>
                  Your submission failed
                  <br />
                  {get('[0].message', errors)}
                </span>
              ) : (
                t('Your lineup has been successfully updated')
              )}
            </NotificationHandler>
          </Container>
        )}
      </Responsive>
    );
  }
}

export default withNotificationCallback(RapidFireTeamSelectionBase);

const EntryTitle = glamorous.h3({
  margin: '1em 0',
  textAlign: 'center'
});

const SelectionAndReturns = glamorous.div({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginLeft: 10
});

const PriceButton = glamorous.button(({ theme, selected }) => ({
  backgroundColor: selected ? theme.color.primary : theme.color.white,
  color: selected ? theme.color.white : theme.color.basis2,
  padding: theme.spacing.small,
  width: 80,
  border: selected ? 'none' : '1px solid',
  borderColor: theme.color.basis2
}));

const Prize = glamorous.div(({ theme }) => ({
  color: theme.color.primary,
  fontWeight: theme.fontWeight.demiBold,
  fontSize: theme.fontSize.large
}));

const ReturnsWrapper = glamorous.div({
  padding: '0 1em'
});

const ButtonsWrapper = glamorous.div({
  paddingTop: '1em',
  textAlign: 'center',
  flexGrow: 1
});

const BottomSection = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'stretch'
});

const Row = glamorous.div({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'start',
  gap: 10
});
